import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";

export interface CampaignProductSelectionState {
  isRequired: boolean;
  isComplete: boolean;
  hasSufficientOptions: boolean;
  totalChoiceCount: number;
  poolSkus: string[];
}

export function buildCampaignProductSelectionState(
  campaign: InfluencerCampaign,
  selectedSkus: string[],
): CampaignProductSelectionState {
  const productAllocations = campaign.productAllocations ?? [];
  const poolSkus = [...new Set(productAllocations.map((allocation) => allocation.sku))];
  const productGroups = campaign.productGroups ?? [];

  if (productGroups.length > 0) {
    const knownGroupIds = new Set(
      productGroups.map((productGroup) => productGroup.groupId),
    );
    const allocationBySku = new Map(
      productAllocations.map((allocation) => [allocation.sku, allocation]),
    );
    const selectedCountByGroupId = new Map<string, number>();
    let hasInvalidSelection = new Set(selectedSkus).size !== selectedSkus.length;

    for (const selectedSku of selectedSkus) {
      const selectedAllocation = allocationBySku.get(selectedSku);
      if (
        !selectedAllocation ||
        selectedAllocation.disabled ||
        !selectedAllocation.groupId ||
        !knownGroupIds.has(selectedAllocation.groupId)
      ) {
        hasInvalidSelection = true;
        continue;
      }
      selectedCountByGroupId.set(
        selectedAllocation.groupId,
        (selectedCountByGroupId.get(selectedAllocation.groupId) ?? 0) + 1,
      );
    }

    const totalChoiceCount = productGroups.reduce(
      (total, productGroup) => total + productGroup.choiceCount,
      0,
    );
    const hasSufficientOptions = productGroups.every((productGroup) => {
      const availableOptionCount = productAllocations.filter(
        (allocation) =>
          allocation.groupId === productGroup.groupId && !allocation.disabled,
      ).length;
      return (
        Number.isInteger(productGroup.choiceCount) &&
        productGroup.choiceCount > 0 &&
        availableOptionCount >= productGroup.choiceCount
      );
    });
    const isComplete =
      !hasInvalidSelection &&
      hasSufficientOptions &&
      selectedSkus.length === totalChoiceCount &&
      productGroups.every(
        (productGroup) =>
          (selectedCountByGroupId.get(productGroup.groupId) ?? 0) ===
          productGroup.choiceCount,
      );

    return {
      isRequired: true,
      isComplete,
      hasSufficientOptions,
      totalChoiceCount,
      poolSkus,
    };
  }

  const totalChoiceCount = campaign.reward?.productChoiceCount ?? 0;
  if (totalChoiceCount <= 0) {
    return {
      isRequired: false,
      isComplete: true,
      hasSufficientOptions: true,
      totalChoiceCount: 0,
      poolSkus,
    };
  }

  const selectablePoolSkus = new Set(
    productAllocations
      .filter((allocation) => !allocation.disabled)
      .map((allocation) => allocation.sku),
  );
  const hasUniqueSelections = new Set(selectedSkus).size === selectedSkus.length;
  const hasOnlySelectableSkus = selectedSkus.every((sku) =>
    selectablePoolSkus.has(sku),
  );
  return {
    isRequired: true,
    isComplete:
      hasUniqueSelections &&
      hasOnlySelectableSkus &&
      selectedSkus.length === totalChoiceCount,
    hasSufficientOptions: selectablePoolSkus.size >= totalChoiceCount,
    totalChoiceCount,
    poolSkus,
  };
}

export function canSelectCampaignProductSku(
  campaign: InfluencerCampaign,
  selectedSkus: string[],
  sku: string,
): boolean {
  if (selectedSkus.includes(sku)) return true;

  const allocation = (campaign.productAllocations ?? []).find(
    (productAllocation) => productAllocation.sku === sku,
  );
  if (!allocation || allocation.disabled) return false;

  const productGroups = campaign.productGroups ?? [];
  if (productGroups.length > 0) {
    const productGroup = productGroups.find(
      (group) => group.groupId === allocation.groupId,
    );
    if (!productGroup) return false;

    const allocationBySku = new Map(
      (campaign.productAllocations ?? []).map((productAllocation) => [
        productAllocation.sku,
        productAllocation,
      ]),
    );
    const selectedCountForGroup = selectedSkus.filter(
      (selectedSku) =>
        allocationBySku.get(selectedSku)?.groupId === productGroup.groupId,
    ).length;
    return selectedCountForGroup < productGroup.choiceCount;
  }

  const selectionState = buildCampaignProductSelectionState(
    campaign,
    selectedSkus,
  );
  return (
    selectionState.isRequired &&
    selectedSkus.length < selectionState.totalChoiceCount
  );
}
